import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import FlagModal from "../components/common/FlagModal";
import { BsFlagFill } from "react-icons/bs";

const Quiz = () => {
  const userId = localStorage.getItem("userId");
  const [searchParams] = useSearchParams();
  const challengeId = searchParams.get("challengeId");
  const [challengeData, setChallengeData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState("");
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0); // In seconds
  const navigate = useNavigate();
  const baseUrl = import.meta.env.VITE_APP_BASE_URL;
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [selectedReason, setSelectedReason] = useState("");
  const [otherText, setOtherText] = useState("");
  const [paused, setPaused] = useState(false);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const inviteCode = queryParams.get("inviteCode");

  const handleFlagSubmit = async () => {
    if (!currentQuestion || !currentQuestion.id || !selectedReason) return;
    try {
      const token = localStorage.getItem("token");
      const reasonToSend =
        selectedReason === "Other, please specify" ? otherText : selectedReason;
      const response = await fetch(`${baseUrl}/play/questions/flag`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          questionId: currentQuestion.id,
          reason: reasonToSend,
          userId,
        }),
      });
      if (!response.ok) throw new Error("Failed to flag question");
      toast.success("Flag submitted!");
    } catch (err) {
      toast.error("Failed to submit flag.");
    } finally {
      setShowFlagModal(false);
      setSelectedReason("");
      setOtherText("");
      setPaused(false);
    }
  };

  // Fetch questions
  useEffect(() => {
    const fetchQuestions = async () => {
      if (!challengeId) {
        setError("Challenge ID not found.");
        setLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${baseUrl}/play/challenges/${challengeId}/start`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        );
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || "Failed to fetch questions.");
        }

        const data = await response.json();
        setChallengeData(data);
        setQuestions(data.questions);
        setTimeLeft(data.numberOfQuestions * 5); // ⏱ Total time
        setLoading(false);
      } catch (error) {
        setError(error.message);
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [challengeId, baseUrl]);

  // Countdown timer effect
  useEffect(() => {
    if (!timeLeft || paused) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmitQuiz();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, paused]);

  const currentQuestion = questions[currentQuestionIndex];

  const handleAnswerSelect = (option) => {
    setSelectedAnswer(option);
  };

  const handleNextQuestion = () => {
    setAnswers({ ...answers, [currentQuestionIndex]: selectedAnswer });
    setSelectedAnswer("");
    setCurrentQuestionIndex((prev) => prev + 1);
  };

  const handleSubmitQuiz = async () => {
    const finalAnswers = { ...answers };
    if (selectedAnswer) {
      finalAnswers[currentQuestionIndex] = selectedAnswer;
    }

    try {
      const token = localStorage.getItem("token");
      for (const [index, answer] of Object.entries(finalAnswers)) {
        await fetch(`${baseUrl}/play/challenges/${challengeId}/answer`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            userId,
            questionIndex: parseInt(index),
            answer,
          }),
        });
      }

      navigate(
        `/practice/results?challengeId=${challengeId}&inviteCode=${inviteCode}`,
      );
    } catch (error) {
      console.error("Error submitting answers:", error);
      toast.error("Failed to submit answers.");
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
      .toString()
      .padStart(2, "0");
    const secs = (seconds % 60).toString().padStart(2, "0");
    return `${mins}:${secs}`;
  };

  if (loading)
    return (
      <div className="h-screen flex justify-center items-center">
        Loading...
      </div>
    );
  if (error)
    return (
      <div className="h-screen flex justify-center items-center text-red-600">
        {error}
      </div>
    );
  if (!currentQuestion)
    return (
      <div className="h-screen flex justify-center items-center">
        Submitting...
      </div>
    );

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-2xl font-semibold">
            Question {currentQuestionIndex + 1}
          </h2>
          {challengeData && (
            <div className="text-sm text-gray-600">
              <span>Subject: {challengeData.subject}</span>
              <span className="mx-2">•</span>
              <span>Topic: {challengeData.topic}</span>
              <span className="mx-2">•</span>
              <span>Level: {challengeData.level}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-4">
          <div className="text-xl text-red-600 font-mono">
            Time Left: {formatTime(timeLeft)}
          </div>
          <button
            onClick={() => {
              setShowFlagModal(true);
              setPaused(true);
            }}
            className="text-red-600 text-sm hover:underline"
          >
            <BsFlagFill />
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <p className="text-lg mb-6">{currentQuestion.question}</p>
        <div className="space-y-3">
          {currentQuestion.options.map((option, index) => (
            <div
              key={index}
              onClick={() => handleAnswerSelect(option)}
              className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                selectedAnswer === option
                  ? "border-indigo-500 bg-indigo-50"
                  : "border-gray-200 hover:border-indigo-300 hover:bg-gray-50"
              }`}
            >
              <div className="flex items-center">
                <div
                  className={`h-5 w-5 mr-4 rounded-full flex items-center justify-center border ${
                    selectedAnswer === option
                      ? "border-indigo-500 bg-indigo-500 text-white"
                      : "border-gray-300"
                  }`}
                >
                  {selectedAnswer === option && "✓"}
                </div>
                <span className="text-gray-700">{option}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <FlagModal
        visible={showFlagModal}
        onClose={() => {
          setShowFlagModal(false);
          setPaused(false);
          setOtherText("");
        }}
        onSubmit={handleFlagSubmit}
        selectedReason={selectedReason}
        setSelectedReason={setSelectedReason}
        questionId={currentQuestion?.id}
        otherText={otherText}
        setOtherText={setOtherText}
      />

      <div className="flex justify-end">
        {currentQuestionIndex < questions.length - 1 ? (
          <button
            onClick={handleNextQuestion}
            disabled={!selectedAnswer}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-400"
          >
            Next Question
          </button>
        ) : (
          <button
            onClick={handleSubmitQuiz}
            disabled={!selectedAnswer}
            className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:bg-gray-400"
          >
            Submit Quiz
          </button>
        )}
      </div>
    </div>
  );
};

export default Quiz;
